import { HOST, ENDPOINTS } from '../../constants/api';
import { Application } from '../../core/app';
import { showToast } from '../../toast';

/**
 * Authentik OAuth2 authentication module.
 * This provides an alternative to Firebase/Google auth.
 *
 * Flow:
 * 1. Frontend calls backend to get the Authentik authorization URL
 * 2. User is redirected to Authentik login page
 * 3. After authenticating, Authentik redirects back to our backend callback
 * 4. Backend exchanges code for token, resolves user, redirects to frontend with ID token
 * 5. Frontend extracts token from URL, stores it, and sets up axios interceptor
 */

/**
 * Start the Authentik OAuth2 login flow.
 * This redirects the user to Authentik's authorization page.
 */
export const signInWithAuthentik = async () => {
    try {
        // First, get the authorization URL from our backend
        const response = await fetch(`${HOST}${ENDPOINTS.authentikLoginUrl}`);
        
        if (!response.ok) {
            if (response.status === 500) {
                showToast('Authentication service misconfigured. Please contact an administrator.', { severity: 'error' });
                return;
            }
            showToast('Failed to initiate sign-in. Please try again.', { severity: 'error' });
            return;
        }

        const data = await response.json();
        
        if (!data.loginUrl) {
            showToast('Failed to get sign-in URL.', { severity: 'error' });
            return;
        }

        // Redirect to Authentik authorization page
        window.location.href = data.loginUrl;
    } catch (err) {
        console.error('Authentik sign-in error:', err);
        showToast('Network error. Please check your connection.', { severity: 'error' });
    }
};

/**
 * Handle the Authentik callback - extract token from URL and set up auth.
 * Returns true if a token was found and processed, false otherwise.
 */
export const handleAuthentikCallback = () => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const refreshToken = params.get('refresh_token');
    const provider = params.get('provider');
    const error = params.get('error');

    if (error) {
        handleAuthentikError(error);
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
        return true;
    }

    if (provider === 'authentik' && token) {
        // Clean up URL immediately to avoid token exposure in browser history
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Set provider FIRST so setAccessToken knows to use localStorage
        Application.setAuthProvider('authentik');
        
        // Store token in memory + localStorage (persists across app restarts)
        Application.setAccessToken(token);
        
        // Store refresh token in localStorage for auto-refresh
        if (refreshToken) {
            localStorage.setItem('authentik_refresh_token', refreshToken);
        }
        
        return true;
    }

    return false;
};

/**
 * Handle Authentik authentication errors.
 */
function handleAuthentikError(errorCode) {
    const messages = {
        'csrf': 'Security validation failed. Please try signing in again.',
        'missing_code': 'Authentication code missing. Please try again.',
        'server_config': 'Authentication service misconfigured. Please contact an administrator.',
        'auth_failed': 'Authentication failed. Please try again.',
        'no_id_token': 'No identity token received. Please try again.',
        'no_user_id': 'Unable to identify user. Please try again.',
    };
    showToast(messages[errorCode] || 'Authentication failed. Please try again.', { severity: 'error' });
}

/**
 * Check if there's a cached Authentik token in localStorage.
 * Stored in localStorage (not sessionStorage) so tokens persist across
 * app restarts — critical for PWA/Chrome app usage on mobile.
 */
export const getAuthentikToken = () => {
    return localStorage.getItem('authentik_token');
};

/**
 * Store an Authentik token in localStorage for persistence across app restarts.
 */
export const storeAuthentikToken = (token) => {
    localStorage.setItem('authentik_token', token);
};

/**
 * Clear the cached Authentik token.
 */
export const clearAuthentikToken = () => {
    localStorage.removeItem('authentik_token');
    localStorage.removeItem('authentik_refresh_token');
};

/**
 * Get the stored refresh token.
 */
export const getAuthentikRefreshToken = () => {
    return localStorage.getItem('authentik_refresh_token');
};

/**
 * Refresh an expired Authentik token using the backend refresh endpoint.
 * Returns a promise that resolves with the new token or null on failure.
 */
export const refreshAuthentikToken = async () => {
    const refreshToken = getAuthentikRefreshToken();
    if (!refreshToken) {
        return null;
    }

    try {
        const response = await fetch(`${HOST}${ENDPOINTS.authentikRefresh}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: `refresh_token=${encodeURIComponent(refreshToken)}`,
        });

        if (!response.ok) {
            // 401 means refresh token is also expired/invalid
            return null;
        }

        const data = await response.json();
        if (data.token) {
            // Store new token in memory + localStorage
            Application.setAccessToken(data.token);
            if (data.refresh_token) {
                localStorage.setItem('authentik_refresh_token', data.refresh_token);
            }
            return data.token;
        }
        return null;
    } catch (err) {
        console.error('Authentik token refresh failed:', err);
        return null;
    }
};
